import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { applyGoogleConsentMode, getStoredConsent, isConsentValid, loadPosthogAnalytics } from "../lib/cookieConsent";
import CookieConsentBanner from "../components/CookieConsentBanner";
import GlobalStatusBanner from "../components/GlobalStatusBanner";
import PageErrorBoundary from "../components/PageErrorBoundary";
import { Building2, Mail, Menu, X } from "lucide-react";

function NewsletterForm() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");
  const [checking, setChecking] = useState(false);
  const [exists, setExists] = useState(false);

  useEffect(() => {
    const cleanEmail = email.trim();
    if (!cleanEmail || !cleanEmail.includes("@")) {
      setExists(false);
      setChecking(false);
      return undefined;
    }

    let cancelled = false;
    setChecking(true);
    const timer = window.setTimeout(async () => {
      try {
        const { data } = await api.get("/newsletter/check", { params: { email: cleanEmail } });
        if (!cancelled) setExists(Boolean(data.exists));
      } catch {
        if (!cancelled) setExists(false);
      } finally {
        if (!cancelled) setChecking(false);
      }
    }, 350);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [email]);

  const submit = async (e) => {
    e.preventDefault();
    const cleanName = name.trim();
    const cleanEmail = email.trim();
    if (!cleanName || !cleanEmail || exists || checking) return;
    setStatus("loading");
    try {
      const { data } = await api.post("/newsletter/subscribe", { name: cleanName, email: cleanEmail });
      setStatus(data?.already_subscribed ? "exists" : "ok");
      setEmail("");
      setName("");
      setExists(false);
    } catch {
      setStatus("error");
    }
  };
  return (
    <form onSubmit={submit} className="grid gap-2" data-testid="newsletter-form">
      <div className="grid sm:grid-cols-2 gap-2">
        <input
          type="text"
          required
          placeholder="Nume și prenume"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setStatus("idle");
          }}
          className="w-full px-4 py-3 bg-white border border-slate-300 rounded-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-600 focus:border-sky-600"
          data-testid="newsletter-name-input"
        />
        <div>
          <input
            type="email"
            required
            placeholder="adresa@email.ro"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setStatus("idle");
              setExists(false);
            }}
            className="w-full px-4 py-3 bg-white border border-slate-300 rounded-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-600 focus:border-sky-600"
            data-testid="newsletter-email-input"
          />
          {exists && <div className="mt-1 text-xs text-rose-700">Acest email este deja abonat.</div>}
          {!exists && checking && <div className="mt-1 text-xs text-slate-500">Verificăm emailul...</div>}
        </div>
      </div>
      <button
        type="submit"
        disabled={status === "loading" || exists || checking}
        className="px-6 py-3 bg-sky-700 text-white font-medium rounded-sm hover:bg-sky-800 transition-colors disabled:opacity-50"
        data-testid="newsletter-submit-btn"
      >
        {status === "loading" ? "Se trimite..." : exists ? "Email deja abonat" : "Abonează-te"}
      </button>
      {status === "ok" && <span className="text-emerald-700 text-sm" data-testid="newsletter-success">Mulțumim! Te-ai abonat cu succes.</span>}
      {status === "exists" && <span className="text-rose-700 text-sm">Acest email este deja abonat.</span>}
      {status === "error" && <span className="text-rose-700 text-sm">Eroare. Încearcă din nou.</span>}
    </form>
  );
}

export default function PublicLayout() {
  const [open, setOpen] = useState(false);
  const [pages, setPages] = useState([]);
  const [cookieSettings, setCookieSettings] = useState(null);
  const [cookieConsent, setCookieConsent] = useState(() => getStoredConsent());
  const loc = useLocation();
  useEffect(() => { setOpen(false); window.scrollTo(0, 0); }, [loc.pathname]);
  useEffect(() => {
    api.get("/pages").then((r) => setPages(r.data)).catch(() => setPages([]));
    api.get("/site-settings/cookie-consent").then((r) => setCookieSettings(r.data)).catch(() => setCookieSettings(null));
  }, []);

  useEffect(() => {
    if (cookieSettings) {
      applyGoogleConsentMode(cookieSettings, isConsentValid(cookieSettings, cookieConsent) ? cookieConsent : null);
    }
  }, [cookieSettings, cookieConsent]);

  useEffect(() => {
    const consentOk = cookieSettings?.enabled && isConsentValid(cookieSettings, cookieConsent) && Boolean(cookieConsent?.analytics);
    if (!consentOk) return undefined;
    const token = "phc_xAvL2Iq4tFmANRE7kzbKwaSqp1HJjN7x48s3vr0CMjs";
    const schedule = window.requestIdleCallback || ((cb) => window.setTimeout(cb, 1200));
    const cancel = window.cancelIdleCallback || window.clearTimeout;
    const handle = schedule(() => loadPosthogAnalytics(token));
    return () => cancel(handle);
  }, [cookieSettings, cookieConsent]);

  useEffect(() => {
    const gaId = (cookieSettings?.google_analytics_id || "").trim();
    const consentOk = cookieSettings?.enabled && isConsentValid(cookieSettings, cookieConsent) && Boolean(cookieConsent?.analytics);
    if (!gaId || !consentOk || typeof window === "undefined" || !window.gtag) return;
    window.gtag("event", "page_view", {
      page_path: `${loc.pathname}${loc.search}`,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [loc.pathname, loc.search, cookieSettings, cookieConsent]);

  const navLink = ({ isActive }) =>
    `text-sm font-medium transition-colors ${isActive ? "text-sky-700" : "text-slate-700 hover:text-slate-900"}`;
  const pageLabel = (page) => (page.slug === "faqs" ? "FAQ" : page.title);
  const pagesTree = useMemo(() => {
    const byId = new Map(pages.map((page) => [page.id, { ...page, children: [] }]));
    const roots = [];
    for (const page of byId.values()) {
      if (page.parent_page_id && byId.has(page.parent_page_id)) {
        byId.get(page.parent_page_id).children.push(page);
      } else {
        roots.push(page);
      }
    }
    const sortTree = (items) => {
      items.sort((a, b) => (Number(a.display_order) || 0) - (Number(b.display_order) || 0) || a.title.localeCompare(b.title));
      items.forEach((item) => sortTree(item.children || []));
    };
    sortTree(roots);
    return roots;
  }, [pages]);

  return (
    <div className="App bg-slate-50">
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="logo-home-link">
            <div className="w-9 h-9 bg-slate-900 text-white flex items-center justify-center rounded-sm">
              <Building2 size={20} />
            </div>
            <div className="leading-tight">
              <div className="font-display font-bold text-slate-900 tracking-tight text-[15px]">Modernizăm Bacăul</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Portal investiții publice</div>
            </div>
          </Link>
          <nav className="hidden lg:flex items-center gap-7">
            <NavLink to="/investitii" className={navLink} data-testid="nav-investments">Investiții</NavLink>
            <NavLink to="/harta-investitiilor" className={navLink} data-testid="nav-map">Harta</NavLink>
            <NavLink to="/centralizator-investitii" className={navLink} data-testid="nav-centralizator">Centralizator</NavLink>
            <NavLink to="/administratia-viziteu" className={navLink} data-testid="nav-administration">Administrația</NavLink>
            <NavLink to="/faqs" className={navLink} data-testid="nav-faqs">FAQ</NavLink>
          </nav>
          <button
            className="lg:hidden text-slate-700"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="meniu"
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
        {open && (
          <div className="lg:hidden border-t border-slate-200 bg-white">
            <div className="px-4 py-3 flex flex-col gap-1">
              {[
                ["/investitii", "Investiții"],
                ["/harta-investitiilor", "Harta"],
                ["/centralizator-investitii", "Centralizator"],
                ["/administratia-viziteu", "Administrația"],
                ["/faqs", "FAQ"],
              ].map(([to, label]) => (
                <NavLink key={to} to={to} className={({ isActive }) => `px-3 py-2 rounded-sm text-sm font-medium ${isActive ? "bg-sky-50 text-sky-700" : "text-slate-700"}`}>
                  {label}
                </NavLink>
              ))}
            </div>
          </div>
        )}
      </header>

      <GlobalStatusBanner />
      <main className="flex-1">
        <PageErrorBoundary>
          <Outlet />
        </PageErrorBoundary>
      </main>

      <footer className="bg-slate-900 text-slate-300 mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid md:grid-cols-3 gap-12">
          <div>
            <div className="flex items-center gap-2.5 mb-4">
              <div className="w-9 h-9 bg-white text-slate-900 flex items-center justify-center rounded-sm">
                <Building2 size={20} />
              </div>
              <div className="leading-tight">
                <div className="font-display font-bold text-white tracking-tight text-sm leading-none">Modernizăm Bacăul</div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 leading-none mt-1">Portal investiții publice</div>
              </div>
            </div>
            <p className="text-sm text-slate-400 leading-relaxed">
              Platforma oficială a Municipiului Bacău pentru transparența investițiilor publice.
              Fiecare proiect, fiecare leu — vizibil pentru cetățeni.
            </p>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-4">Navigare rapidă</div>
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/investitii" className="hover:text-white transition-colors">Listă investiții</Link></li>
              <li><Link to="/harta-investitiilor" className="hover:text-white transition-colors">Harta investițiilor</Link></li>
              <li><Link to="/centralizator-investitii" className="hover:text-white transition-colors">Centralizator</Link></li>
              <li><Link to="/faqs" className="hover:text-white transition-colors">FAQ</Link></li>
              {pagesTree.map((page) => (
                <li key={page.id}>
                  <Link to={`/${page.slug}`} className="hover:text-white transition-colors">
                    {pageLabel(page)}
                  </Link>
                  {page.children?.length > 0 && (
                    <ul className="ml-4 mt-2 space-y-2 text-slate-400">
                      {page.children.map((child) => (
                        <li key={child.id}>
                          <Link to={`/${child.slug}`} className="hover:text-white transition-colors">
                            {pageLabel(child)}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-4 flex items-center gap-2"><Mail size={12}/> Abonează-te la newsletter</div>
            <p className="text-sm text-slate-400 mb-4">Primește lunar un raport al investițiilor publice din Bacău.</p>
            <NewsletterForm />
            <p className="text-xs text-slate-500 mt-3">
              Prin abonare accepți <Link to="/politica-de-confidentialitate" className="underline">politica de confidențialitate</Link>.
            </p>
          </div>
        </div>
        <div className="border-t border-slate-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 text-xs text-slate-500 flex flex-col sm:flex-row justify-between gap-2">
            <span>© {new Date().getFullYear()} Municipiul Bacău · Demo Modernizăm Bacăul</span>
            <span>Construit pentru transparență publică</span>
          </div>
        </div>
      </footer>
      <CookieConsentBanner
        settings={cookieSettings}
        consent={cookieConsent}
        onConsentSaved={(next) => {
          setCookieConsent(next);
          if (cookieSettings) {
            applyGoogleConsentMode(cookieSettings, next);
          }
        }}
      />
    </div>
  );
}
